import { CheckCircle, Error, Help, MoneyOff, PendingActions } from '@mui/icons-material'
import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { range } from 'ramda'
import { useEffect, useState } from 'react'
import { Field, Form, FormSpy } from 'react-final-form'
import { DataPanel } from '../../components/DataPanel'
import {
    getBlockchainIdentities,
    getEscrowContracts,
    getSelfBlockchainIdentity,
} from '../../services/blockchainService'
import { BlockchainIdentityResponse } from '../../services/models/blockchain-identity'
import { formatNumberWithThousandSeparator } from '../../utils'
import BaseView from '../BaseView'
import ContractDetailsDialog from './ContractDetailsDialog'

const currentYear = dayjs().get('year')

// eslint-disable-next-line etc/no-commented-out-code
// interface AddNewContractDialogProps {
//     open: boolean
//     onClose: () => void
//     onSubmit: () => void
//     counterParties: { name: string; did: string }[]
//     thisParty: { name: string; type: 'payer' | 'provider' }
// }

// TODO: add new
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// const AddNewContractDialog = (props: AddNewContractDialogProps) => {
//     const [counterPartyDid, setCounterPartyDid] = useState<string>()

//     return (
//         <Dialog open={props.open}>
//             <DialogTitle>Add New Contract</DialogTitle>
//             <DialogContent>
//                 <Form onSubmit={() => {}}>
//                     {(formProps) => (
//                         <Grid container spacing={2}>
//                             <FormSpy
//                                 subscription={{ values: true }}
//                                 onChange={(formState) => {
//                                     const { values } = formState
//                                     if (values.counterParty !== counterPartyDid) {
//                                         setCounterPartyDid(values.counterParty)
//                                         const providerName =
//                                             props.thisParty.type === 'provider'
//                                                 ? props.thisParty.name
//                                                 : props.counterParties.find(
//                                                       ({ did }) => values.counterParty === did,
//                                                   )
//                                         const payerName =
//                                             props.thisParty.type === 'payer'
//                                                 ? props.thisParty.name
//                                                 : props.counterParties.find(
//                                                       ({ did }) => values.counterParty === did,
//                                                   )
//                                         formProps.form.change(
//                                             'contractName',
//                                             `${providerName}_${payerName}_${currentYear}`,
//                                         ) // TODO version
//                                     }
//                                 }}
//                             />
//                             <Grid item xs={4}>
//                                 <Field name="counterParty">
//                                     {(fieldProps) => (
//                                         <Select {...fieldProps.input} label="Counter Party">
//                                             {props.counterParties.map(({ name, did }) => (
//                                                 <MenuItem key={did} value={did}>
//                                                     {name}
//                                                 </MenuItem>
//                                             ))}
//                                         </Select>
//                                     )}
//                                 </Field>
//                             </Grid>
//                             <Grid item xs={4}>
//                                 <Field name="contractName">
//                                     {(fieldProps) => (
//                                         <TextField {...fieldProps.input} label="Contract Name" />
//                                     )}
//                                 </Field>
//                             </Grid>
//                             <Grid item xs={4}>
//                                 <Field name="providerEthAddresses">
//                                     {(fieldProps) =>
//                                         fieldProps.input.value.length ? (
//                                             fieldProps.input.value.map((fieldValue, i) => (
//                                                 <TextField
//                                                     value={fieldValue}
//                                                     onChange={(e) =>
//                                                         formProps.form.change(
//                                                             'providerEthAddresses',
//                                                             formProps.form
//                                                                 .getState()
//                                                                 .values.providerEthAddresses.map(
//                                                                     (lastValue, lastIndex) =>
//                                                                         lastIndex === i
//                                                                             ? e.target.value
//                                                                             : lastValue,
//                                                                 ),
//                                                         )
//                                                     }
//                                                 />
//                                             ))
//                                         ) : (
//                                             <TextField {...fieldProps.input} />
//                                         )
//                                     }
//                                 </Field>
//                             </Grid>
//                         </Grid>
//                     )}
//                 </Form>
//             </DialogContent>
//         </Dialog>
//     )
// }

const Contracts = () => {
    const [loading, setLoading] = useState(false)
    const [counterParties, setCounterParties] = useState<{
        available: BlockchainIdentityResponse
        selected?: string
    }>({
        available: [],
        selected: undefined,
    })
    const [orgRegisteredAs, setOrgRegisteredAs] = useState<{
        type: 'payer' | 'provider'
        name: string
        orgDid: string
    }>()
    const [escrowContracts, setEscrowContracts] = useState<any[]>([])
    const [selectedContract, setSelectedContract] = useState<any>()
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)

    const filterablePlanYears = ['Any Year', ...range(currentYear - 4, currentYear + 2)]

    useEffect(() => {
        getSelfBlockchainIdentity().then(({ data: { orgType, orgName, did } }) => {
            setOrgRegisteredAs({ type: orgType, name: orgName, orgDid: did })
            getBlockchainIdentities(orgType === 'payer' ? 'provider' : 'payer').then(
                ({ data: _counterParites }) => setCounterParties({ available: _counterParites }),
            )
        })
    }, [])

    const refreshList = async ({ values }: any) => {
        setLoading(true)
        const counterParty = values.counterParty === 'anyone' ? undefined : values.counterParty
        const payerDid = orgRegisteredAs?.type === 'payer' ? orgRegisteredAs.orgDid : counterParty
        const providerDid =
            orgRegisteredAs?.type === 'provider' ? orgRegisteredAs.orgDid : counterParty

        const { data: _contracts } = await getEscrowContracts({
            active: values.active,
            payerDid,
            providerDid,
            year: values.planYear === 'Any Year' ? undefined : values.planYear,
            includeFunds: true,
        })
        setEscrowContracts(
            _contracts.map((contract: any) => {
                let numTokens = 0
                try {
                    numTokens = parseFloat(contract.funds.freeFunds)
                } catch (err) {
                    console.error('Cannot parse ETH value of', contract.funds.freeFunds)
                }
                let status = ''
                if (!contract.active || numTokens === 0) status = 'Inactive'
                else if (numTokens < 10_000) status = 'Warning'
                else if (contract.isDraft) status = 'Draft'
                else status = 'Active'

                return { ...contract, status }
            }),
        )
        setLoading(false)
    }

    const getSubmittedByFriendlyName = (contract: any) => {
        if (contract.submittedBy === contract.senderOrg.did) return contract.senderOrg.companyName
        if (contract.submittedBy === contract.receiverOrg.did)
            return contract.receiverOrg.companyName
        return contract.submittedBy
    }

    const renderContractStatus = (contract: any) => {
        const { status } = contract
        switch (status) {
            case 'Active':
                return <CheckCircle />
            case 'Inactive':
                return <MoneyOff />
            case 'Draft':
                return <PendingActions />
            case 'Warning':
                return <Error />
            default:
                return <Help />
        }
    }

    return (
        <Box>
            <ContractDetailsDialog
                open={detailsDialogOpen}
                onClose={() => setDetailsDialogOpen(false)}
                contract={selectedContract}
            />
            <BaseView
                title="Contracts"
                tabNames={['My contracts', 'Medicare fee schedule']}
                subviews={[
                    // eslint-disable-next-line react/jsx-key
                    <DataPanel
                        customTitleContent={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="h6" sx={{ marginRight: 2 }}>
                                    Filter Contracts By
                                </Typography>
                                <Form
                                    initialValues={{
                                        planYear: 'Any Year',
                                        active: true,
                                        counterParty: 'anyone',
                                    }}
                                    onSubmit={() => {}}
                                    render={() => (
                                        <>
                                            <FormSpy
                                                subscription={{ values: true }}
                                                onChange={refreshList}
                                            />
                                            <Stack direction="row" spacing={2}>
                                                <Field name="active" type="checkbox">
                                                    {(fieldProps) => (
                                                        <FormControlLabel
                                                            label="Active"
                                                            labelPlacement="start"
                                                            control={
                                                                <Switch
                                                                    {...fieldProps.input}
                                                                    // checked={fieldProps.input.value}
                                                                />
                                                            }
                                                        />
                                                    )}
                                                </Field>
                                                <Field name="planYear">
                                                    {(fieldProps) => (
                                                        <Box sx={{ display: 'flex' }}>
                                                            <Typography
                                                                sx={{
                                                                    alignSelf: 'center',
                                                                    marginRight: 1,
                                                                }}
                                                            >
                                                                Plan Year
                                                            </Typography>
                                                            <Select
                                                                sx={{ height: '40px' }}
                                                                value={fieldProps.input.value}
                                                                onChange={fieldProps.input.onChange}
                                                            >
                                                                {filterablePlanYears.map((year) => (
                                                                    <MenuItem
                                                                        key={year}
                                                                        value={year}
                                                                    >
                                                                        {year}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </Box>
                                                    )}
                                                </Field>
                                                <Field name="counterParty">
                                                    {(fieldProps) => (
                                                        <Box sx={{ display: 'flex' }}>
                                                            <Typography
                                                                sx={{
                                                                    alignSelf: 'center',
                                                                    marginRight: 1,
                                                                }}
                                                            >
                                                                With
                                                            </Typography>
                                                            <Select
                                                                sx={{ height: '40px' }}
                                                                {...fieldProps.input}
                                                            >
                                                                <MenuItem value="anyone">
                                                                    Anyone
                                                                </MenuItem>
                                                                {counterParties.available.map(
                                                                    (counterParty) => (
                                                                        <MenuItem
                                                                            key={counterParty.did}
                                                                            value={counterParty.did}
                                                                        >
                                                                            {counterParty.orgName}
                                                                        </MenuItem>
                                                                    ),
                                                                )}
                                                            </Select>
                                                        </Box>
                                                    )}
                                                </Field>
                                            </Stack>
                                        </>
                                    )}
                                />
                            </Box>
                        }
                        widget={<Button disabled>Add New</Button>} // TODO
                        content={
                            loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Status</TableCell>
                                            <TableCell>With</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>
                                                Payment Allocated / In Escrow / Paid
                                            </TableCell>
                                            <TableCell>Effective Start</TableCell>
                                            <TableCell>Effective End</TableCell>
                                            <TableCell>Created On</TableCell>
                                            <TableCell>Submitted By</TableCell>
                                            <TableCell>Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {escrowContracts?.map((contract, i) => (
                                            <TableRow key={contract.name + i}>
                                                <TableCell>
                                                    {renderContractStatus(contract)}
                                                </TableCell>
                                                <TableCell>
                                                    {orgRegisteredAs?.type === 'payer'
                                                        ? contract.receiverOrg.companyName
                                                        : contract.senderOrg.companyName}
                                                </TableCell>
                                                <TableCell>{contract.name}</TableCell>
                                                <TableCell>{`${formatNumberWithThousandSeparator(
                                                    parseFloat(contract.funds.freeFunds),
                                                )} / ${formatNumberWithThousandSeparator(
                                                    parseFloat(contract.funds.amountInEscrow),
                                                )} / ${formatNumberWithThousandSeparator(
                                                    parseFloat(contract.funds.totalAmountPaid),
                                                )}`}</TableCell>
                                                <TableCell>
                                                    {dayjs(contract.startDate * 1000).format(
                                                        'YYYY-MM-DD',
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {dayjs(contract.endDate * 1000).format(
                                                        'YYYY-MM-DD',
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {dayjs(contract.savedOnBlockchain).format(
                                                        'YYYY-MM-DD',
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {getSubmittedByFriendlyName(contract)}
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        onClick={() => {
                                                            setSelectedContract(contract)
                                                            setDetailsDialogOpen(true)
                                                        }}
                                                    >
                                                        Details
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )
                        }
                    />,
                ]}
            />
        </Box>
    )
}

export default Contracts
